import { useState, useEffect, useCallback } from "react";

const API    = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");

const api = async (path, opts = {}) => {
  const res = await fetch(API + path, {
    ...opts,
    headers: { "Content-Type":"application/json", ...(getToken()?{Authorization:"Bearer "+getToken()}:{}), ...opts.headers },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
};

const S = {
  card:    { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:14 },
  cardHdr: { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", justifyContent:"space-between", fontWeight:600, fontSize:13 },
  btn:     (c="#25D366") => ({ padding:"8px 16px", borderRadius:7, fontSize:12, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
  btnOut:  { padding:"8px 16px", borderRadius:7, fontSize:12, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
  input:   { width:"100%", padding:"8px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:13, fontFamily:"inherit", outline:"none", marginBottom:10 },
  label:   { fontSize:11, color:"#6b7280", fontWeight:600, display:"block", marginBottom:3 },
  toggle:  (on) => ({ width:44, height:24, borderRadius:12, background:on?"#25D366":"#d1d5db", position:"relative", cursor:"pointer", transition:"background .2s", flexShrink:0 }),
  knob:    (on) => ({ width:20, height:20, borderRadius:"50%", background:"#fff", position:"absolute", top:2, left:on?22:2, transition:"left .2s", boxShadow:"0 1px 3px rgba(0,0,0,0.2)" }),
};

const NOTIFICATION_TYPES = [
  { id:"order",    label:"New Order",         icon:"🛍️", desc:"When a new order is received on any platform" },
  { id:"payment",  label:"Payment Received",  icon:"💰", desc:"When a payment is confirmed for an order" },
  { id:"lowstock", label:"Low Stock Alert",   icon:"⚠️", desc:"When product stock falls below threshold" },
  { id:"shipment", label:"Shipment Dispatched",icon:"🚚", desc:"When a shipment AWB is generated" },
];

export default function PageWhatsApp({ toast, products = [], orders = [] }) {
  const [phone,    setPhone]    = useState(localStorage.getItem("wa_phone") || "");
  const [enabled,  setEnabled]  = useState(JSON.parse(localStorage.getItem("wa_enabled") || "{}"));
  const [testing,  setTesting]  = useState(false);
  const [sending,  setSending]  = useState("");
  const [logs,     setLogs]     = useState(JSON.parse(localStorage.getItem("wa_logs") || "[]"));
  const [tab,      setTab]      = useState("settings");
  const [customMsg,setCustomMsg]= useState({ phone:"", message:"" });
  const [digestEnabled, setDigestEnabled] = useState(localStorage.getItem("wa_digest_enabled")==="true");
  const [digestPhone,   setDigestPhone]   = useState(localStorage.getItem("wa_digest_phone")||"");
  const [digestTime,    setDigestTime]    = useState(localStorage.getItem("wa_digest_time")||"08:00");
  const [digestPreview, setDigestPreview] = useState("");
  const [digestLoading, setDigestLoading] = useState(false);
  const [digestSending, setDigestSending] = useState(false);
  const [digestData,    setDigestData]    = useState(null);

  // low stock products
  const lowStockProducts = products.filter((p) =>
    Object.values(p.inventory || {}).some((v) => v > 0 && v <= (p.lowAlert || 10))
  );

  const savePhone = () => {
    localStorage.setItem("wa_phone", phone);
    toast("Phone saved ✓", "success");
  };

  const toggleNotif = (id) => {
    const updated = { ...enabled, [id]: !enabled[id] };
    setEnabled(updated);
    localStorage.setItem("wa_enabled", JSON.stringify(updated));
    toast((updated[id] ? "Enabled" : "Disabled") + " — " + NOTIFICATION_TYPES.find((t) => t.id === id)?.label, "success");
  };

  const addLog = (type, status, message) => {
    const log = { id: Date.now(), type, status, message, time: new Date().toLocaleString("en-IN") };
    const updated = [log, ...logs].slice(0, 50);
    setLogs(updated);
    localStorage.setItem("wa_logs", JSON.stringify(updated));
  };

  const testConnection = async () => {
    if (!phone) { toast("Enter your WhatsApp number first", "error"); return; }
    setTesting(true);
    try {
      await api("/whatsapp/test", { method:"POST", body:JSON.stringify({ phone }) });
      toast("✅ Test message sent to " + phone, "success");
      addLog("test", "success", "Test message sent to " + phone);
    } catch(e) {
      toast(e.message, "error");
      addLog("test", "error", e.message);
    } finally { setTesting(false); }
  };

  const sendLowStockAlert = async () => {
    if (!phone) { toast("Enter your WhatsApp number first", "error"); return; }
    if (lowStockProducts.length === 0) { toast("No low stock products found", "error"); return; }
    setSending("lowstock");
    try {
      const prods = lowStockProducts.map((p) => ({
        name:  p.name,
        stock: Object.values(p.inventory || {}).reduce((a, b) => a + b, 0),
      }));
      await api("/whatsapp/notify/lowstock", { method:"POST", body:JSON.stringify({ phone, products:prods }) });
      toast("Low stock alert sent ✓", "success");
      addLog("lowstock", "success", lowStockProducts.length + " products alerted");
    } catch(e) {
      toast(e.message, "error");
      addLog("lowstock", "error", e.message);
    } finally { setSending(""); }
  };

  const sendOrderAlert = async (order) => {
    if (!phone) { toast("Enter your WhatsApp number first", "error"); return; }
    setSending("order_" + order.id);
    try {
      await api("/whatsapp/notify/order", {
        method: "POST",
        body: JSON.stringify({
          phone,
          orderNumber:  order.name || order.id,
          customerName: order.customer ? order.customer.first_name + " " + order.customer.last_name : "Customer",
          amount:       order.total_price,
          items:        (order.line_items || []).slice(0,3).map((l) => l.name).join(", "),
        }),
      });
      toast("Order notification sent ✓", "success");
      addLog("order", "success", "Order " + (order.name || order.id) + " notified");
    } catch(e) {
      toast(e.message, "error");
      addLog("order", "error", e.message);
    } finally { setSending(""); }
  };

  const sendCustom = async () => {
    if (!customMsg.phone || !customMsg.message) { toast("Enter phone and message", "error"); return; }
    setSending("custom");
    try {
      await api("/whatsapp/send", { method:"POST", body:JSON.stringify(customMsg) });
      toast("Message sent ✓", "success");
      addLog("custom", "success", "Custom message to " + customMsg.phone);
      setCustomMsg((f) => ({ ...f, message:"" }));
    } catch(e) {
      toast(e.message, "error");
      addLog("custom", "error", e.message);
    } finally { setSending(""); }
  };

  const tabStyle = (active) => ({
    padding:"10px 20px", background:"none", border:"none",
    borderBottom: active ? "2px solid #25D366" : "2px solid transparent",
    color: active ? "#25D366" : "#6b7280",
    fontWeight: active ? 600 : 400, fontSize:13, cursor:"pointer", fontFamily:"inherit",
  });

  const saveDigestSettings = async () => {
    localStorage.setItem("wa_digest_enabled", digestEnabled);
    localStorage.setItem("wa_digest_phone",   digestPhone);
    localStorage.setItem("wa_digest_time",    digestTime);
    try {
      await api("/whatsapp/digest/settings", { method:"POST", body:JSON.stringify({ enabled:digestEnabled, phone:digestPhone, time:digestTime }) });
      toast("Digest settings saved ✓", "success");
    } catch(e) { toast(e.message, "error"); }
  };

  const previewDigest = async () => {
    setDigestLoading(true);
    try {
      const data = await api("/whatsapp/digest/preview", { method:"POST", body:JSON.stringify({}) });
      setDigestPreview(data.preview);
      setDigestData(data.data);
      toast("Preview generated ✓", "success");
    } catch(e) { toast(e.message, "error"); }
    setDigestLoading(false);
  };

  const sendDigestNow = async () => {
    if (!digestPhone) { toast("Enter WhatsApp number", "error"); return; }
    setDigestSending(true);
    try {
      await api("/whatsapp/digest/send", { method:"POST", body:JSON.stringify({ phone:digestPhone }) });
      toast("✅ Digest sent to " + digestPhone, "success");
      addLog("digest", "success", "Daily digest sent to " + digestPhone);
    } catch(e) { toast(e.message, "error"); addLog("digest", "error", e.message); }
    setDigestSending(false);
  };

  return (
    <div>
      {/* header */}
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
        <div style={{ width:48, height:48, borderRadius:12, background:"#25D366", display:"flex", alignItems:"center", justifyContent:"center", fontSize:24 }}>💬</div>
        <div>
          <div style={{ fontWeight:700, fontSize:18 }}>WhatsApp Notifications</div>
          <div style={{ fontSize:13, color:"#6b7280" }}>Send real-time alerts via WhatsApp Business API</div>
        </div>
        {lowStockProducts.length > 0 && (
          <div style={{ marginLeft:"auto", background:"#fffbeb", border:"1px solid #fde68a", borderRadius:8, padding:"8px 14px", fontSize:12, color:"#92400e" }}>
            ⚠️ {lowStockProducts.length} products low on stock
            <button style={{ ...S.btn(), marginLeft:10, padding:"4px 10px", fontSize:11 }} onClick={sendLowStockAlert} disabled={!!sending}>
              {sending==="lowstock" ? "Sending…" : "Alert Now"}
            </button>
          </div>
        )}
      </div>

      {/* tabs */}
      <div style={{ display:"flex", borderBottom:"1px solid #e5e7eb", marginBottom:16 }}>
        {[{id:"settings",label:"Settings"},{id:"orders",label:"Order Alerts"},{id:"custom",label:"Custom Message"},{id:"logs",label:"Logs ("+logs.length+")"}].map((t)=>(
          <button key={t.id} style={tabStyle(tab===t.id)} onClick={()=>setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* ── SETTINGS TAB ── */}
      {tab==="settings" && (
        <>
          {/* phone setup */}
          <div style={S.card}>
            <div style={S.cardHdr}><span>Your WhatsApp Number</span></div>
            <div style={{ padding:16 }}>
              <label style={S.label}>WhatsApp Number (for receiving alerts)</label>
              <div style={{ display:"flex", gap:8 }}>
                <input style={{ ...S.input, marginBottom:0, flex:1 }} placeholder="+91 98XXXXXXXX" value={phone} onChange={(e)=>setPhone(e.target.value)}/>
                <button style={S.btn()} onClick={savePhone}>Save</button>
                <button style={{ ...S.btn("#F47920"), opacity:testing?0.7:1 }} onClick={testConnection} disabled={testing}>
                  {testing ? "Sending…" : "Test"}
                </button>
              </div>
              <div style={{ fontSize:11, color:"#6b7280", marginTop:6 }}>
                Enter your number with country code. Click Test to receive a test message.
              </div>
            </div>
          </div>

          {/* notification toggles */}
          <div style={S.card}>
            <div style={S.cardHdr}><span>Notification Settings</span></div>
            <div style={{ padding:16 }}>
              {NOTIFICATION_TYPES.map((notif) => (
                <div key={notif.id} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 0", borderBottom:"1px solid #f3f4f6" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                    <span style={{ fontSize:24 }}>{notif.icon}</span>
                    <div>
                      <div style={{ fontWeight:600, fontSize:14 }}>{notif.label}</div>
                      <div style={{ fontSize:12, color:"#6b7280" }}>{notif.desc}</div>
                    </div>
                  </div>
                  <div style={S.toggle(enabled[notif.id])} onClick={()=>toggleNotif(notif.id)}>
                    <div style={S.knob(enabled[notif.id])}/>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* API status */}
          <div style={{ ...S.card, padding:16 }}>
            <div style={{ fontWeight:600, fontSize:13, marginBottom:12 }}>WhatsApp API Status</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:10 }}>
              {[
                { label:"Phone Number ID", value:process.env.REACT_APP_WA_PHONE_ID || "Configured on server", color:"#15803d" },
                { label:"API Version",     value:"v18.0",                                                      color:"#2563eb" },
                { label:"Webhook",         value:"/api/whatsapp/webhook",                                      color:"#7c3aed" },
                { label:"Status",          value:process.env.WHATSAPP_TOKEN ? "Connected" : "Check server env",color:"#15803d" },
              ].map((item) => (
                <div key={item.label} style={{ background:"#f9fafb", borderRadius:8, padding:"10px 12px" }}>
                  <div style={{ fontSize:10, color:"#9ca3af", marginBottom:3 }}>{item.label}</div>
                  <div style={{ fontWeight:600, fontSize:12, color:item.color }}>{item.value}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── ORDERS TAB ── */}
      {tab==="orders" && (
        <div style={S.card}>
          <div style={S.cardHdr}>
            <span>Send Order Notifications</span>
            <span style={{ fontSize:12, color:"#9ca3af" }}>{orders.length} orders</span>
          </div>
          {orders.length === 0 ? (
            <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>No orders loaded</div>
          ) : (
            <div style={{ overflowX:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead><tr>
                  {["Order","Customer","Amount","Status",""].map((h) => (
                    <th key={h} style={{ padding:"8px 12px", textAlign:"left", fontSize:11, fontWeight:600, color:"#6b7280", background:"#f9fafb" }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {orders.slice(0,20).map((o) => (
                    <tr key={o.id} onMouseEnter={(e)=>e.currentTarget.style.background="#fafafa"} onMouseLeave={(e)=>e.currentTarget.style.background=""}>
                      <td style={{ padding:"9px 12px", fontFamily:"monospace", fontSize:11, color:"#6b7280" }}>{o.name}</td>
                      <td style={{ padding:"9px 12px", fontWeight:500 }}>{o.customer ? o.customer.first_name+" "+o.customer.last_name : "Guest"}</td>
                      <td style={{ padding:"9px 12px", fontWeight:600 }}>₹{parseFloat(o.total_price||0).toLocaleString("en-IN")}</td>
                      <td style={{ padding:"9px 12px" }}>
                        <span style={{ background:o.financial_status==="paid"?"#f0fdf4":"#fffbeb", color:o.financial_status==="paid"?"#15803d":"#d97706", padding:"2px 8px", borderRadius:20, fontSize:11, fontWeight:600 }}>
                          {o.financial_status || "pending"}
                        </span>
                      </td>
                      <td style={{ padding:"9px 12px" }}>
                        <button style={{ ...S.btn(), fontSize:11, padding:"4px 12px", opacity:sending==="order_"+o.id?0.7:1 }}
                          onClick={()=>sendOrderAlert(o)} disabled={!!sending}>
                          {sending==="order_"+o.id ? "Sending…" : "📲 Notify"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── CUSTOM MESSAGE TAB ── */}
      {tab==="custom" && (
        <div style={S.card}>
          <div style={S.cardHdr}><span>Send Custom WhatsApp Message</span></div>
          <div style={{ padding:20, maxWidth:500 }}>
            <label style={S.label}>WhatsApp Number</label>
            <input style={S.input} placeholder="+91 98XXXXXXXX" value={customMsg.phone} onChange={(e)=>setCustomMsg((f)=>({...f,phone:e.target.value}))}/>
            <label style={S.label}>Message</label>
            <textarea style={{ ...S.input, height:120, resize:"vertical" }} placeholder="Type your message…" value={customMsg.message} onChange={(e)=>setCustomMsg((f)=>({...f,message:e.target.value}))}/>
            <div style={{ fontSize:11, color:"#9ca3af", marginBottom:14 }}>{customMsg.message.length} characters</div>
            <button style={{ ...S.btn(), width:"100%", padding:"10px", fontSize:14, opacity:sending==="custom"?0.7:1 }}
              onClick={sendCustom} disabled={!!sending}>
              {sending==="custom" ? "Sending…" : "📲 Send Message"}
            </button>
          </div>
        </div>
      )}

      {/* ── LOGS TAB ── */}
      {tab==="logs" && (
        <div style={S.card}>
          <div style={S.cardHdr}>
            <span>Notification Logs</span>
            <button style={S.btnOut} onClick={()=>{ setLogs([]); localStorage.removeItem("wa_logs"); toast("Logs cleared","success"); }}>Clear</button>
          </div>
          {logs.length===0 ? (
            <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>No logs yet</div>
          ) : (
            <div style={{ padding:16 }}>
              {logs.map((log) => (
                <div key={log.id} style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"10px 0", borderBottom:"1px solid #f3f4f6" }}>
                  <span style={{ fontSize:18 }}>{log.status==="success"?"✅":"❌"}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:2 }}>
                      <span style={{ fontWeight:600, fontSize:13 }}>{log.type.charAt(0).toUpperCase()+log.type.slice(1)}</span>
                      <span style={{ fontSize:11, color:"#9ca3af" }}>{log.time}</span>
                    </div>
                    <div style={{ fontSize:12, color:"#6b7280" }}>{log.message}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══ DAILY DIGEST ══ */}
      {tab === "digest" && (
        <div>
          {/* Hero card */}
          <div style={{ background:"linear-gradient(135deg,#25D366,#128C7E)", borderRadius:10, padding:"20px 22px", marginBottom:14, color:"#fff" }}>
            <div style={{ fontWeight:800, fontSize:18, marginBottom:6 }}>🌅 Daily AI Digest on WhatsApp</div>
            <div style={{ fontSize:13, opacity:.9, lineHeight:1.6, marginBottom:12 }}>
              Every morning at 8 AM — yesterday's revenue, top SKU, stock alerts and one AI insight — sent directly to your WhatsApp.
            </div>
            <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
              {["📊 Revenue & Orders","🏆 Top SKU","⚠️ Stock Alerts","🤖 AI Insight"].map((f)=>(
                <span key={f} style={{ background:"rgba(255,255,255,0.2)", borderRadius:20, padding:"3px 12px", fontSize:11, fontWeight:600 }}>{f}</span>
              ))}
            </div>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            {/* Left — Settings */}
            <div>
              <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:14 }}>
                <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13 }}>⚙️ Digest Settings</div>
                <div style={{ padding:16 }}>
                  {/* Enable toggle */}
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14, padding:"10px 12px", background:"#f9fafb", borderRadius:8 }}>
                    <div>
                      <div style={{ fontWeight:600, fontSize:13 }}>Enable Daily Digest</div>
                      <div style={{ fontSize:11, color:"#9ca3af" }}>Sends every day at 8:00 AM IST</div>
                    </div>
                    <div style={S.toggle(digestEnabled)} onClick={()=>setDigestEnabled(!digestEnabled)}>
                      <div style={S.knob(digestEnabled)}/>
                    </div>
                  </div>

                  <label style={S.label}>WhatsApp Number (with country code)</label>
                  <input style={S.input} placeholder="+91 98765 43210" value={digestPhone}
                    onChange={(e)=>setDigestPhone(e.target.value)}/>

                  <label style={S.label}>Send Time (IST)</label>
                  <input type="time" style={{ ...S.input, marginBottom:14 }} value={digestTime}
                    onChange={(e)=>setDigestTime(e.target.value)}/>

                  <div style={{ display:"flex", gap:8 }}>
                    <button style={{ ...S.btn(), flex:1, justifyContent:"center" }} onClick={saveDigestSettings}>
                      💾 Save Settings
                    </button>
                    <button style={{ ...S.btn("#6366f1"), flex:1, justifyContent:"center", opacity:digestLoading?0.7:1 }}
                      onClick={previewDigest} disabled={digestLoading}>
                      {digestLoading ? "⏳ Generating…" : "👁 Preview"}
                    </button>
                  </div>

                  <button style={{ ...S.btn("#25D366"), width:"100%", marginTop:8, justifyContent:"center", opacity:digestSending?0.7:1 }}
                    onClick={sendDigestNow} disabled={digestSending||!digestPhone}>
                    {digestSending ? "⏳ Sending…" : "📤 Send Digest Now (Test)"}
                  </button>
                </div>
              </div>

              {/* What's included */}
              <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb" }}>
                <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13 }}>📋 What's Included</div>
                <div style={{ padding:14 }}>
                  {[
                    ["📦","Yesterday's Orders","Total orders received across all platforms"],
                    ["💰","Daily Revenue","Total revenue with average order value"],
                    ["🏆","Top SKU","Best-selling product of the day"],
                    ["❌","Out of Stock","Products with zero inventory"],
                    ["⚠️","Low Stock Alerts","Products below minimum threshold"],
                    ["🤖","AI Insight","One actionable tip from Claude AI based on your data"],
                  ].map(([icon,title,desc])=>(
                    <div key={title} style={{ display:"flex", gap:10, marginBottom:10 }}>
                      <span style={{ fontSize:18, flexShrink:0 }}>{icon}</span>
                      <div>
                        <div style={{ fontWeight:600, fontSize:12 }}>{title}</div>
                        <div style={{ fontSize:11, color:"#6b7280" }}>{desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right — Preview */}
            <div>
              <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb" }}>
                <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <span>📱 Message Preview</span>
                  {digestPreview && (
                    <button onClick={()=>navigator.clipboard.writeText(digestPreview).then(()=>toast("Copied ✓","success"))}
                      style={{ ...S.btnOut, fontSize:11, padding:"3px 10px" }}>Copy</button>
                  )}
                </div>
                <div style={{ padding:16 }}>
                  {!digestPreview && !digestLoading && (
                    <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>
                      <div style={{ fontSize:48, marginBottom:12 }}>📱</div>
                      <div style={{ fontWeight:600, marginBottom:6 }}>No preview yet</div>
                      <div style={{ fontSize:12, marginBottom:14 }}>Click Preview to generate today's digest</div>
                      <button style={S.btn("#6366f1")} onClick={previewDigest}>👁 Generate Preview</button>
                    </div>
                  )}
                  {digestLoading && (
                    <div style={{ textAlign:"center", padding:40, color:"#6366f1" }}>
                      <div style={{ fontSize:36, marginBottom:10 }}>🤖</div>
                      <div style={{ fontWeight:600 }}>Generating AI digest…</div>
                    </div>
                  )}
                  {digestPreview && !digestLoading && (
                    <>
                      {/* WhatsApp-style bubble */}
                      <div style={{ background:"#DCF8C6", borderRadius:"0 12px 12px 12px", padding:"12px 14px", fontSize:12, lineHeight:1.7, fontFamily:"inherit", whiteSpace:"pre-wrap", marginBottom:12, boxShadow:"0 1px 2px rgba(0,0,0,0.1)" }}>
                        {digestPreview.replace(/\*(.*?)\*/g, (_, m) => m).replace(/_(.*?)_/g, (_, m) => m)}
                      </div>
                      {/* Stats */}
                      {digestData && (
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                          {[
                            ["Orders", digestData.orderCount],
                            ["Revenue", "₹"+digestData.totalRevenue?.toLocaleString("en-IN")],
                            ["Low Stock", digestData.lowStockItems?.length+" items"],
                            ["Out of Stock", digestData.outOfStock?.length+" items"],
                          ].map(([l,v])=>(
                            <div key={l} style={{ background:"#f9fafb", borderRadius:7, padding:"8px 10px" }}>
                              <div style={{ fontSize:10, color:"#9ca3af" }}>{l}</div>
                              <div style={{ fontWeight:700, fontSize:14 }}>{v}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Setup instructions */}
              <div style={{ background:"#fffbeb", borderRadius:10, border:"1px solid #fde68a", padding:"12px 14px", marginTop:14 }}>
                <div style={{ fontWeight:700, fontSize:12, color:"#92400e", marginBottom:8 }}>⚙️ Setup Required</div>
                {[
                  "Add WHATSAPP_TOKEN and WHATSAPP_PHONE_ID to Render environment variables",
                  "Enter your WhatsApp number above (must be registered with WhatsApp Business)",
                  "Click Save Settings to enable the 8 AM daily cron job",
                  "Click Send Now to test immediately",
                ].map((step, i)=>(
                  <div key={i} style={{ fontSize:11, color:"#92400e", display:"flex", gap:8, marginBottom:4 }}>
                    <span style={{ background:"#f59e0b", color:"#fff", borderRadius:"50%", width:16, height:16, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:700, flexShrink:0 }}>{i+1}</span>
                    {step}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
